import React, { useCallback, useEffect, useRef, useState } from 'react';
import { router } from '@inertiajs/react';
import api, { ApiError } from '../../lib/api';
import type { AiPromptResponse, HandoffTarget } from '../../types/aiHandoff';

// 「扔给 AI」浮层。target 为 null 时不渲染 —— 调用方靠这一个 prop 控制开合，
// 不用再传一个 open。
//
// 单条级与清单级是同一个组件：只有取数、抬头卡片、离线那句提示三处随 kind 变，
// chips / 正文 / 底部那一行一个字都不改（chips 本来就读服务端给的 actions）。
//
// 提示词在服务端拼：那四行护栏是产品的一部分（agent 不许改任务本体这条规则
// 的执行处），不该散在 TSX 里。这里只负责取、改、复制。
//
// 改动不存回任务：存回去它就成了任务上的第三个正文字段，而那个字段没有读者。
const AiHandoffSheet: React.FC<{ target: HandoffTarget | null; onClose: () => void }> = ({
  target,
  onClose,
}) => {
  // 当前是哪个动作不留本地状态 —— data.action 是服务端规范化之后的值，那才是
  // 真的。留一份本地的就会出现「传了个非法值、chip 亮的和正文对不上」。
  const [data, setData] = useState<AiPromptResponse | null>(null);
  const [text, setText] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 「直接执行」是另一条通道（WorklogLinkerService），跟上面这份取提示词的
  // 状态完全独立——两者互不影响，可以同时存在。
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState<string | null>(null);
  // 成功之后按钮不能变回可点——完成提示只是按钮旁边一行不起眼的小字，
  // 用户很容易没看清就以为没反应，点第二次会真的再跑一遍、写出重复留言
  // （2026-09-04 线上实测踩到过：52 秒内两条内容几乎一样的留言）。
  const [runSucceeded, setRunSucceeded] = useState(false);
  // 人手改过之后，切动作要先问一句。用 ref 不用 state：它只在事件里读，
  // 变了不需要重渲染。
  const dirty = useRef(false);

  const load = useCallback(
    (next: string) => {
      if (!target) return;
      setError(null);

      const request =
        target.kind === 'task'
          ? api.aiPrompt.show(target.task.id, next)
          : api.aiPrompt.list(target.view, target.projectId, next);

      request
        .then((raw) => {
          const res = raw as AiPromptResponse;
          setData(res);
          setText(res.prompt);
          dirty.current = false;
        })
        .catch(() => setError('取不到提示词，刷新页面再试'));
    },
    [target]
  );

  // 换作用对象时重取。传空串 = 让服务端挑默认动作，前端不需要知道默认是哪个
  // —— 清单级的默认还随清单变（回收站那一栏一个动作都没有）。
  useEffect(() => {
    if (!target) return;
    setCopied(false);
    setRunResult(null);
    setRunSucceeded(false);
    load('');
  }, [target, load]);

  useEffect(() => {
    if (!target) return undefined;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [target, onClose]);

  if (!target) return null;

  const choose = (next: string) => {
    if (next === data?.action) return;
    // 切动作会把手改的内容冲掉，而那可能是你刚敲了两分钟的一段补充。
    // 用 confirm 不引组件库 —— /todo 刻意把 antd 挡在外面。
    if (dirty.current && !window.confirm('换动作会覆盖你改过的内容，继续？')) return;
    setCopied(false);
    setRunResult(null);
    setRunSucceeded(false);
    load(next);
  };

  // 「直接执行」只在单条级任务、串起来这个动作、且服务端配置了 AI 时才可能
  // 触发——按钮本身在 server_ai_available 为假时不渲染，这里再判一遍
  // target.kind 纯粹是让 TypeScript 收窄类型，不是防误触（按钮都没出现，
  // 点不到这个函数）。
  const runDirectly = async () => {
    if (target.kind !== 'task') return;
    setRunning(true);
    setRunResult(null);
    try {
      await api.worklogLink.create(target.task.id);
      setRunResult('已经写进任务留言了 ✓ 关掉浮层去看');
      setRunSucceeded(true);
      // 留言、结构化关联都已经落库——刷新页面数据，不用用户自己手动刷新
      // 才看到。失败不刷：没有新东西可看，刷了只会打断用户看错误提示。
      router.reload();
    } catch (err) {
      setRunResult(err instanceof ApiError ? err.message : '执行失败，稍后再试');
    } finally {
      setRunning(false);
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    // 不自动关：你可能要连着复制两个动作
    window.setTimeout(() => setCopied(false), 1400);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 px-5 pb-5 pt-[7vh]"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      role="presentation"
    >
      <div className="flex max-h-[86vh] w-full max-w-[620px] flex-col overflow-hidden rounded-xl border border-todo-line bg-todo-card shadow-2xl">
        <div className="px-4 pt-[14px]">
          <div className="flex items-center gap-2">
            <h2 className="text-[15px] font-semibold">扔给 AI</h2>
            <button type="button" onClick={onClose} aria-label="关闭" className="ml-auto text-todo-muted">
              ✕
            </button>
          </div>
          <div className="mt-[9px] rounded-lg border border-todo-line bg-todo-card2 px-[11px] py-2">
            {target.kind === 'task' ? (
              <>
                <div className="text-[13px] font-medium">{target.task.title}</div>
                <div className="mt-[3px] break-all font-mono text-[11.5px] text-todo-muted">
                  {`${window.location.origin}/todo/${target.task.url_key}`}　·　task_id{' '}
                  {target.task.id}
                </div>
              </>
            ) : (
              <>
                <div className="text-[13px] font-medium">
                  清单《{target.name}》· {target.count} 条
                </div>
                <div className="mt-[3px] break-all font-mono text-[11.5px] text-todo-muted">
                  {`${window.location.origin}${
                    target.view === 'project'
                      ? `/todo/projects/${target.projectId}`
                      : `/todo/${target.view}`
                  }`}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 px-4 pb-[10px] pt-3">
          {(data?.actions ?? []).map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => choose(item.key)}
              className={[
                'rounded-full border px-3 py-[5px] text-[12.5px]',
                item.key === 'custom' ? 'border-dashed' : '',
                data?.action === item.key
                  ? 'border-transparent bg-todo-accent2 font-semibold text-todo-accent'
                  : 'border-todo-line bg-todo-card text-todo-ink2',
              ].join(' ')}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4">
          <textarea
            value={text}
            spellCheck={false}
            aria-label="提示词"
            onChange={(event) => {
              setText(event.target.value);
              dirty.current = true;
            }}
            className="min-h-[232px] w-full resize-y overflow-auto whitespace-pre rounded-lg border border-todo-line bg-todo-card2 px-[13px] py-3 font-mono text-[12px] leading-[1.72] text-todo-ink outline-none focus:border-todo-accent"
          />
          <p className="mt-[7px] text-[11.5px] leading-relaxed text-todo-muted">
            {target.kind === 'task'
              ? 'AI 改不了这条任务 —— 结论要你自己贴回来。正文是复制这一刻的快照，会过期。这段可改，改动不存回任务。'
              : 'AI 挪不了任务 —— 该动哪几条要你自己动。名单最多抄 12 条，而且会过期。这段可改，改动不存回任务。'}
          </p>
          {error && <p className="mt-1 text-[12px] text-todo-urgent">{error}</p>}
        </div>

        {/*
          「直接执行」只在单条级 + 串起来 + 服务端配置了 AI 时出现——三个条件
          缺一都不渲染，不是禁用状态。没配置 AI 的账号，这个 UI 分支完全不
          存在，不会露出一个点了必错的按钮（见 WorklogLinkerService.configured?）。
        */}
        {target.kind === 'task' && data?.action === 'link_worklog' && data?.server_ai_available && (
          <div className="flex items-center gap-2.5 border-t border-todo-line bg-todo-card2 px-4 py-2.5">
            <div className="min-w-0 flex-1 text-[11.5px] text-todo-muted">
              {runResult ?? '也可以让 asaptodo 自己跑一遍，不用你贴进别的对话框。'}
            </div>
            <button
              type="button"
              onClick={() => void runDirectly()}
              disabled={running || runSucceeded}
              className="rounded-lg border border-todo-line bg-todo-card px-[14px] py-[7px] text-[12.5px] font-medium text-todo-ink disabled:opacity-60"
            >
              {running ? '执行中…' : runSucceeded ? '已完成 ✓' : '直接执行'}
            </button>
          </div>
        )}

        <div className="flex items-center gap-2.5 border-t border-todo-line bg-todo-card2 px-4 py-3">
          <div className="min-w-0 flex-1 text-[11.5px] text-todo-muted">
            贴进任何一个 AI 对话框都行 —— 这段不依赖 MCP。
          </div>
          <button
            type="button"
            onClick={() => void copy()}
            className="rounded-lg bg-todo-accent px-[18px] py-2 text-[13px] font-medium text-white"
          >
            {copied ? '已复制 ✓' : '复制'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AiHandoffSheet;
